import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDispatch } from 'react-redux';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { SectionSchedulerService } from '../../api/services/sectionSchedulerService';

type DraggableItem = {
  _id: string;
  sku: string;
  new_price_tier1: number | null;
  new_price_tier2: number | null;
};

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  PENDING: 'warning',
  APPLIED: 'success',
  TERMINATED: 'default',
};

const HEALTH_COLORS: Record<
  IProductSectionScheduleResponse['health'],
  'success' | 'warning' | 'error'
> = {
  OK: 'success',
  STUCK_PENDING: 'warning',
  STUCK_APPLIED: 'error',
};

const extractApiError = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

const toDatetimeLocal = (iso: string) => (iso ? iso.slice(0, 16) : '');
const fromDatetimeLocal = (val: string) => (val ? val + ':00' : '');

const newDraggableItem = (): DraggableItem => ({
  _id: crypto.randomUUID(),
  sku: '',
  new_price_tier1: null,
  new_price_tier2: null,
});

const fromResponseItem = (item: ISectionScheduleItemResponse): DraggableItem => ({
  _id: crypto.randomUUID(),
  sku: item.sku,
  new_price_tier1: item.new_price_tier1,
  new_price_tier2: item.new_price_tier2,
});

// Inline sortable row for the items table
function SortableItemRow({
  item,
  index,
  onUpdate,
  onRemove,
}: Readonly<{
  item: DraggableItem;
  index: number;
  onUpdate: (id: string, field: keyof Omit<DraggableItem, '_id'>, value: unknown) => void;
  onRemove: (id: string) => void;
}>) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item._id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ width: 40, pr: 0 }}>
        <IconButton size="small" sx={{ cursor: 'grab' }} {...attributes} {...listeners}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell sx={{ width: 40, color: 'text.secondary', fontSize: 12 }}>{index + 1}</TableCell>
      <TableCell>
        <TextField
          size="small"
          value={item.sku}
          onChange={(e) => onUpdate(item._id, 'sku', e.target.value)}
          sx={{ width: 120 }}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          type="number"
          value={item.new_price_tier1 ?? ''}
          onChange={(e) =>
            onUpdate(
              item._id,
              'new_price_tier1',
              e.target.value === '' ? null : Number(e.target.value),
            )
          }
          sx={{ width: 100 }}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          type="number"
          value={item.new_price_tier2 ?? ''}
          onChange={(e) =>
            onUpdate(
              item._id,
              'new_price_tier2',
              e.target.value === '' ? null : Number(e.target.value),
            )
          }
          sx={{ width: 100 }}
        />
      </TableCell>
      <TableCell>
        <IconButton size="small" color="error" onClick={() => onRemove(item._id)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export const SectionSchedulerPage = () => {
  const dispatch = useDispatch();

  const [schedules, setSchedules] = useState<IProductSectionScheduleResponse[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  const [availableSections, setAvailableSections] = useState<IProductSectionSummary[]>([]);
  const [loadingSections, setLoadingSections] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<IProductSectionScheduleResponse | null>(null);
  const [sectionId, setSectionId] = useState(0);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [items, setItems] = useState<DraggableItem[]>([newDraggableItem()]);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<IProductSectionScheduleResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [viewTarget, setViewTarget] = useState<IProductSectionScheduleResponse | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const res = await SectionSchedulerService.getSchedules(page + 1, pageSize, [
        'startDatetime,desc',
      ]);
      setSchedules(res.content);
      setTotalRows(res.total_elements);
    } catch (error) {
      dispatch(
        showNotification({
          message: extractApiError(error, 'Failed to load schedules.'),
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSections = async () => {
    setLoadingSections(true);
    try {
      const sections = await SectionSchedulerService.getAvailableSections();
      setAvailableSections(sections);
    } catch (error) {
      dispatch(
        showNotification({
          message: extractApiError(error, 'Failed to load available sections.'),
          severity: 'error',
        }),
      );
    } finally {
      setLoadingSections(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [page, pageSize]);

  const openCreateDialog = () => {
    setEditTarget(null);
    setSectionId(0);
    setStartTime('');
    setEndTime('');
    setItems([newDraggableItem()]);
    setDialogOpen(true);
    fetchAvailableSections();
  };

  const openEditDialog = (row: IProductSectionScheduleResponse) => {
    setEditTarget(row);
    setSectionId(row.section_id);
    setStartTime(fromDatetimeLocal(toDatetimeLocal(row.start_datetime)));
    setEndTime(fromDatetimeLocal(toDatetimeLocal(row.end_datetime)));
    setItems(
      row.items.length > 0
        ? [...row.items].sort((a, b) => a.sequence - b.sequence).map(fromResponseItem)
        : [newDraggableItem()],
    );
    setDialogOpen(true);
    fetchAvailableSections();
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditTarget(null);
  };

  const buildRequestBody = (): IScheduleSectionRequest => ({
    section_id: sectionId,
    start_time: startTime,
    end_time: endTime,
    items: items.map((item, i) => ({
      sku: item.sku,
      sequence: i + 1,
      new_price_tier1: item.new_price_tier1,
      new_price_tier2: item.new_price_tier2,
    })),
  });

  const handleSave = async () => {
    if (!sectionId) {
      dispatch(showNotification({ message: 'Please select a section.', severity: 'error' }));
      return;
    }
    if (!startTime) {
      dispatch(showNotification({ message: 'Please set a start datetime.', severity: 'error' }));
      return;
    }
    if (items.length === 0) {
      dispatch(showNotification({ message: 'At least one item is required.', severity: 'error' }));
      return;
    }
    const emptySkuIndex = items.findIndex((item) => !item.sku.trim());
    if (emptySkuIndex !== -1) {
      dispatch(
        showNotification({
          message: `Item #${emptySkuIndex + 1} has an empty SKU.`,
          severity: 'error',
        }),
      );
      return;
    }
    setSaving(true);
    try {
      const body = buildRequestBody();
      if (editTarget) {
        await SectionSchedulerService.updateSchedule(editTarget.id, body);
        dispatch(showNotification({ message: 'Schedule updated.' }));
      } else {
        await SectionSchedulerService.createSchedule(body);
        dispatch(showNotification({ message: 'Schedule created.' }));
      }
      closeDialog();
      fetchSchedules();
    } catch (error) {
      dispatch(
        showNotification({
          message: extractApiError(
            error,
            `Failed to ${editTarget ? 'update' : 'create'} schedule.`,
          ),
          severity: 'error',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await SectionSchedulerService.deleteSchedule(deleteTarget.id);
      dispatch(showNotification({ message: 'Schedule deleted.' }));
      setDeleteTarget(null);
      fetchSchedules();
    } catch (error) {
      dispatch(
        showNotification({
          message: extractApiError(error, 'Failed to delete schedule.'),
          severity: 'error',
        }),
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleForceExecute = async (row: IProductSectionScheduleResponse) => {
    try {
      await SectionSchedulerService.forceExecute(row.id);
      dispatch(showNotification({ message: `Schedule #${row.id} force executed.` }));
      fetchSchedules();
    } catch (error) {
      dispatch(
        showNotification({
          message: extractApiError(error, 'Failed to force execute.'),
          severity: 'error',
        }),
      );
    }
  };

  const handleForceTerminate = async (row: IProductSectionScheduleResponse) => {
    try {
      await SectionSchedulerService.forceTerminate(row.id);
      dispatch(showNotification({ message: `Schedule #${row.id} force terminated.` }));
      fetchSchedules();
    } catch (error) {
      dispatch(
        showNotification({
          message: extractApiError(error, 'Failed to force terminate.'),
          severity: 'error',
        }),
      );
    }
  };

  const updateItem = (id: string, field: keyof Omit<DraggableItem, '_id'>, value: unknown) => {
    setItems((prev) => prev.map((item) => (item._id === id ? { ...item, [field]: value } : item)));
  };

  const removeItem = (id: string) => setItems((prev) => prev.filter((item) => item._id !== id));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((item) => item._id === active.id);
      const newIndex = prev.findIndex((item) => item._id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'section_name', headerName: 'Section', flex: 1, minWidth: 140 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) => (
        <Chip label={value} color={STATUS_COLORS[value] ?? 'default'} size="small" />
      ),
    },
    {
      field: 'health',
      headerName: 'Health',
      width: 140,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          color={HEALTH_COLORS[value as IProductSectionScheduleResponse['health']] ?? 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'start_datetime',
      headerName: 'Start',
      width: 170,
      valueFormatter: (value: string) => (value ? value.replace('T', ' ').slice(0, 16) : '—'),
    },
    {
      field: 'end_datetime',
      headerName: 'End',
      width: 170,
      valueFormatter: (value: string) => (value ? value.replace('T', ' ').slice(0, 16) : '—'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 340,
      sortable: false,
      renderCell: ({ row }: { row: IProductSectionScheduleResponse }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
          {row.status === 'PENDING' && (
            <Button
              size="small"
              variant="contained"
              color="success"
              sx={{ textTransform: 'none', fontSize: 11 }}
              onClick={() => handleForceExecute(row)}
            >
              Force Execute
            </Button>
          )}
          {row.status === 'APPLIED' && (
            <Button
              size="small"
              variant="contained"
              color="warning"
              sx={{ textTransform: 'none', fontSize: 11 }}
              onClick={() => handleForceTerminate(row)}
            >
              Force Terminate
            </Button>
          )}
          {row.status === 'PENDING' && (
            <Button
              size="small"
              variant="outlined"
              sx={{ textTransform: 'none', fontSize: 11 }}
              onClick={() => openEditDialog(row)}
            >
              Edit
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none', fontSize: 11 }}
            onClick={() => setViewTarget(row)}
          >
            View
          </Button>
          {row.status === 'PENDING' && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              sx={{ textTransform: 'none', fontSize: 11 }}
              onClick={() => setDeleteTarget(row)}
            >
              Delete
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Section Scheduler
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ textTransform: 'none' }}
        >
          Create Schedule
        </Button>
      </Stack>

      <DataGrid
        rows={schedules}
        columns={columns}
        loading={loading}
        rowCount={totalRows}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        pageSizeOptions={[10, 25, 50]}
        autoHeight
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editTarget ? 'Edit Schedule' : 'Create Schedule'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Section</InputLabel>
              <Select
                label="Section"
                value={sectionId || ''}
                onChange={(e) => setSectionId(Number(e.target.value))}
                disabled={!!editTarget || loadingSections}
              >
                {loadingSections && (
                  <MenuItem disabled value="">
                    Loading sections...
                  </MenuItem>
                )}
                {!loadingSections && availableSections.length === 0 && (
                  <MenuItem disabled value="">
                    No sections available for scheduling
                  </MenuItem>
                )}
                {availableSections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Start datetime"
                type="datetime-local"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={toDatetimeLocal(startTime)}
                onChange={(e) => setStartTime(fromDatetimeLocal(e.target.value))}
              />
              <TextField
                label="End datetime"
                type="datetime-local"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={toDatetimeLocal(endTime)}
                onChange={(e) => setEndTime(fromDatetimeLocal(e.target.value))}
              />
            </Stack>

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Items
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setItems((prev) => [...prev, newDraggableItem()])}
                  sx={{ textTransform: 'none' }}
                >
                  Add Item
                </Button>
              </Stack>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell sx={{ width: 40 }}>#</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Price Tier 1</TableCell>
                      <TableCell>Price Tier 2</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <SortableContext
                    items={items.map((i) => i._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <TableBody>
                      {items.map((item, idx) => (
                        <SortableItemRow
                          key={item._id}
                          item={item}
                          index={idx}
                          onUpdate={updateItem}
                          onRemove={removeItem}
                        />
                      ))}
                    </TableBody>
                  </SortableContext>
                </Table>
              </DndContext>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Read-only view dialog */}
      <Dialog open={!!viewTarget} onClose={() => setViewTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Schedule Details</DialogTitle>
        <DialogContent dividers>
          {viewTarget && (
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Section
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {viewTarget.section_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.3}>
                    <Chip
                      label={viewTarget.status}
                      color={STATUS_COLORS[viewTarget.status] ?? 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Health
                  </Typography>
                  <Box mt={0.3}>
                    <Chip
                      label={viewTarget.health}
                      color={HEALTH_COLORS[viewTarget.health] ?? 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Stack>

              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Start
                  </Typography>
                  <Typography variant="body2">
                    {viewTarget.start_datetime?.replace('T', ' ').slice(0, 16) ?? '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    End
                  </Typography>
                  <Typography variant="body2">
                    {viewTarget.end_datetime?.replace('T', ' ').slice(0, 16) ?? '—'}
                  </Typography>
                </Box>
                {viewTarget.executed_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Executed at
                    </Typography>
                    <Typography variant="body2">
                      {viewTarget.executed_at.replace('T', ' ').slice(0, 16)}
                    </Typography>
                  </Box>
                )}
                {viewTarget.terminated_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Terminated at
                    </Typography>
                    <Typography variant="body2">
                      {viewTarget.terminated_at.replace('T', ' ').slice(0, 16)}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Items
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Price Tier 1</TableCell>
                      <TableCell>Price Tier 2</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...viewTarget.items]
                      .sort((a, b) => a.sequence - b.sequence)
                      .map((item) => (
                        <TableRow key={item.id}>
                          <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                            {item.sequence}
                          </TableCell>
                          <TableCell>{item.sku}</TableCell>
                          <TableCell>{item.new_price_tier1 ?? '—'}</TableCell>
                          <TableCell>{item.new_price_tier2 ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTarget(null)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Schedule</DialogTitle>
        <DialogContent>
          <Typography>
            Delete schedule #{deleteTarget?.id} for <strong>{deleteTarget?.section_name}</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
